import { IApplication } from '../../api/models';
import { ApplicationTableButtons, ApplicationTableColumns } from '../../constants/ApplicationTableItems';
import { MaterialReactTable, MRT_DensityState, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { InfoLink } from '../UIExtentions/InfoLink';
import { AppType } from '../../constants/AppType';
import ApplicationsDelete from './ApplicationsDelete';
import { Loading } from '../Shared/Loading';
import { MRTDefaultTableOptions } from '../../shared/MRTDefaultTableOptions';

export interface ApplicationsTableProps {
    data: IApplication[];
    isLoading: boolean;
    deleteAppHandler?: (appName: string) => void;
    maxHeight?: number;
    hiddenColumns?: [columnName: ApplicationTableColumns];
    hiddenButtons?: [buttonName: ApplicationTableButtons];
}

const ApplicationsTable = ({ data, isLoading, deleteAppHandler, maxHeight, hiddenColumns, hiddenButtons }: ApplicationsTableProps) => {
    const CheckIfButtonShouldBeHidden = useCallback(
        (buttonNm: ApplicationTableButtons) => {
            if (hiddenButtons) {
                return hiddenButtons.includes(buttonNm);
            }

            return false;
        },
        [hiddenButtons]
    );

    const columns = useMemo<MRT_ColumnDef<IApplication>[]>(() => {
        const cols: MRT_ColumnDef<IApplication>[] = [
            {
                header: 'Name',
                id: 'name',
                accessorFn: (row) => row.name,
                Cell: ({ row }) => {
                    return <Link to={`/applications/${row.original.type}/${row.original.name}`}>{row.original.name}</Link>;
                },
                size: 200,
            },
            {
                header: 'Display Name',
                id: 'displayName',
                accessorFn: (row) => row.displayName,
                size: 300,
            },
            {
                header: 'Type',
                id: 'type',
                accessorFn: (row) => row.type,
                filterVariant: 'multi-select',
                filterFn: 'arrIncludesSome',
                size: 100,
            },
            {
                header: 'Actions',
                id: 'actions',
                Cell: ({ row }) => {
                    const keyBase = `action-${row.original.id}`;
                    return (
                        <div key={keyBase}>
                            <InfoLink to={`/applications/${row.original.type}/${row.original.name}`} title={`Details of ${row.original.name}`} />

                            {row.original.type === AppType.WindowsService && !CheckIfButtonShouldBeHidden(ApplicationTableButtons.Delete) ? (
                                <>
                                    |
                                    <ApplicationsDelete name={row.original.name} deleteAppHandler={(appname) => (deleteAppHandler ? deleteAppHandler(appname) : null)} />
                                </>
                            ) : (
                                <></>
                            )}
                        </div>
                    );
                },
                muiTableBodyCellProps: {
                    align: 'center',
                },
                size: 60,
            },
        ];

        return cols;
    }, [CheckIfButtonShouldBeHidden, deleteAppHandler]);

    const initialSorting = [{ id: 'name', desc: false }];
    const columnVisibility = hiddenColumns?.reduce((acc, col) => {
        acc[col] = false;
        return acc;
    }, {} as Record<ApplicationTableColumns, boolean>);

    const tableOptions = {
        ...MRTDefaultTableOptions,
        ...{
            columns,
            data: data,
            enableStickyHeader: true,
            initialState: {
                density: 'compact' as MRT_DensityState,
                sorting: initialSorting,
                columnVisibility: columnVisibility,
                showGlobalFilter: true,
                pagination: { pageSize: 25, pageIndex: 0 },
            },
        },
        ...(maxHeight
            ? {
                  muiTableContainerProps: { sx: { maxHeight: `${maxHeight}px` } },
              }
            : []),
    };
    const table = useMaterialReactTable(tableOptions);

    if (isLoading) {
        return <Loading />;
    }

    return <MaterialReactTable table={table} />;
};

export default ApplicationsTable;
